import React from 'react';
import { MapPin, Users, Wrench, HeartHandshake, Phone, CheckCircle, Building2 } from 'lucide-react';

const features = [
  {
    icon: Users,
    title: "Persönliche Beratung vor Ort",
    description: "Wir kommen zu Ihnen und analysieren Ihre individuelle Situation – kein Callcenter, sondern echte Beratung."
  },
  {
    icon: Building2,
    title: "Planung individueller Energiesysteme",
    description: "Jede Anlage wird speziell für Ihr Gebäude, Ihren Verbrauch und Ihre Ziele geplant."
  },
  {
    icon: Wrench,
    title: "Installation durch erfahrene Fachkräfte",
    description: "Qualifizierte Monteure mit langjähriger Erfahrung sorgen für eine saubere und sichere Installation."
  },
  {
    icon: HeartHandshake,
    title: "Langfristige Betreuung der Anlage",
    description: "Auch nach der Installation sind wir für Sie da – bei Fragen, Service oder Erweiterungen."
  }
];

const RegionalExpertSection = () => {
  return (
    <section data-testid="regional-expert-section" className="py-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-sun-blue/10 text-sun-blue rounded-full text-sm font-semibold mb-4">
            Regional verwurzelt
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            Regionaler
            <span className="text-sun-orange"> Photovoltaik-Spezialist</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            Sunprojects unterstützt Eigenheimbesitzer und Unternehmen in der Region bei Planung und 
            Installation von Photovoltaikanlagen – persönlich, kompetent und zuverlässig.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-16">
          {features.map((feature, index) => {
            const IconComponent = feature.icon;
            return (
              <div
                key={index}
                data-testid={`regional-feature-${index}`}
                className="group bg-white rounded-2xl p-8 border border-slate-100 hover:border-sun-orange/30 hover:shadow-xl hover:shadow-sun-orange/5 transition-all duration-300"
              >
                <div className="flex items-start gap-5">
                  <div className="w-14 h-14 bg-sun-orange/10 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:bg-sun-orange transition-colors">
                    <IconComponent className="w-7 h-7 text-sun-orange group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">{feature.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Regional Info Box */}
        <div className="bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 rounded-3xl p-8 lg:p-12 relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-sun-orange/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-sun-blue/10 rounded-full blur-3xl" />
          
          <div className="relative grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-sun-orange rounded-xl flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white">Ihr Vorteil mit einem regionalen Partner</h3>
              </div>
              
              <p className="text-white/80 text-lg leading-relaxed mb-8">
                Die Zusammenarbeit mit einem regionalen Experten bedeutet kurze Wege, persönliche 
                Ansprechpartner und schnelle Reaktionszeiten. Bei Sunprojects haben Sie immer einen 
                direkten Draht zum Geschäftsführer – nicht zu einem anonymen Callcenter.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-white/90">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span>Schnelle Vor-Ort-Termine in Ihrer Region</span>
                </div>
                <div className="flex items-center gap-3 text-white/90">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span>Direkter Kontakt ohne Warteschleifen</span>
                </div>
                <div className="flex items-center gap-3 text-white/90">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span>Zuverlässige Projektumsetzung vor Ort</span>
                </div>
              </div>
            </div>
            
            {/* Service Areas */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
              <h4 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-sun-orange" />
                Unsere Regionen
              </h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="text-white/90 font-medium">Hessen</div>
                  <ul className="space-y-2 text-white/70 text-sm">
                    <li>• Limburg-Weilburg</li>
                    <li>• Rhein-Main-Gebiet</li>
                    <li>• Gießen & Umgebung</li>
                  </ul>
                </div>
                <div className="space-y-3">
                  <div className="text-white/90 font-medium">Nordrhein-Westfalen</div>
                  <ul className="space-y-2 text-white/70 text-sm">
                    <li>• Köln & Umgebung</li>
                    <li>• Rhein-Erft-Kreis</li>
                    <li>• Bonn & Umgebung</li>
                  </ul>
                </div>
              </div>
              
              <div className="mt-6 pt-6 border-t border-white/10">
                <a 
                  href="tel:+4922359762339" 
                  className="flex items-center gap-3 text-sun-orange hover:text-sun-orange-light transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  <span className="font-semibold">+49 2235 9762339</span>
                </a>
                <p className="text-white/60 text-sm mt-2">
                  Rufen Sie an oder schreiben Sie uns – wir sind für Sie da.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RegionalExpertSection;
